import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./baseQuery";
import { getTimezone, getTimezoneOffset } from "../lib/dateUtils";

/**
 * Kişisel özellikler (mobil uçlar, yalnız JWT): ünvanlar, kaza namazı,
 * haftanın müzakereleri ve değerlendirme, odak seansları. Ay kaydını
 * etkileyen işlemlerden sonra Terakkim kendi ay kaydını tazeler.
 */
export const personalApi = createApi({
  reducerPath: "panelPersonal",
  baseQuery: axiosBaseQuery(),
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ["Titles", "Kaza", "MuzakereWeek", "Assessment", "FocusHistory"],
  endpoints: (builder) => ({
    getMyTitles: builder.query({
      query: () => ({ url: "/api/titles/me", method: "POST", data: {} }),
      providesTags: ["Titles"],
    }),

    // Kaza hesabı; localDate verilirse o gün kılınanlar da (day) gelir.
    getMyKaza: builder.query({
      query: ({ localDate } = {}) => ({
        url: "/api/kaza/me",
        method: "POST",
        data: localDate ? { localDate } : {},
      }),
      providesTags: ["Kaza"],
    }),
    updateKazaSettings: builder.mutation({
      query: ({ mezhep, remaining }) => ({
        url: "/api/kaza/update-settings",
        method: "POST",
        data: { mezhep, remaining },
      }),
      invalidatesTags: ["Kaza"],
    }),
    setKazaDay: builder.mutation({
      query: ({ localDate, today, counts }) => ({
        url: "/api/kaza/set-day",
        method: "POST",
        data: { localDate, today, counts },
      }),
      invalidatesTags: ["Kaza", "Titles"],
    }),

    // Üye olunan grupların haftadaki müzakereleri (katılanlar ve değerlendirme özetiyle).
    getMyWeekMuzakeres: builder.query({
      query: ({ startDate, endDate }) => ({
        url: "/api/muzakeres/my-week",
        method: "POST",
        data: { startDate, endDate },
      }),
      providesTags: ["MuzakereWeek"],
    }),
    setMuzakereDone: builder.mutation({
      query: ({ muzakereId, done, today }) => ({
        url: "/api/muzakeres/set-done",
        method: "POST",
        data: { muzakereId, done, today },
      }),
      invalidatesTags: ["MuzakereWeek", "Titles"],
    }),
    getAssessment: builder.query({
      query: ({ muzakereId }) => ({
        url: "/api/muzakeres/assessment",
        method: "POST",
        data: { muzakereId },
      }),
      providesTags: (result, error, { muzakereId }) => [{ type: "Assessment", id: muzakereId }],
    }),
    submitAssessment: builder.mutation({
      query: ({ muzakereId, answers, today }) => ({
        url: "/api/muzakeres/submit-assessment",
        method: "POST",
        data: { muzakereId, answers, ...(today ? { today } : {}) },
      }),
      invalidatesTags: (result, error, { muzakereId }) => [
        { type: "Assessment", id: muzakereId },
        "MuzakereWeek",
        "Titles",
      ],
    }),

    // Biten odak seansı; hedefe bağlıysa sunucu o hedefin o günkü değerini de artırır.
    addFocusSession: builder.mutation({
      query: ({ minutes, localDate, goalLabel, activity, mode, rounds }) => ({
        url: "/api/daily-logs/add-focus-session",
        method: "POST",
        data: {
          minutes,
          localDate,
          ...(goalLabel ? { goalLabel } : {}),
          ...(goalLabel && activity ? { activity } : {}),
          mode: mode || "odak",
          rounds: rounds || 0,
          timezone: getTimezone(),
          timezoneOffset: getTimezoneOffset(),
        },
      }),
      invalidatesTags: ["FocusHistory", "Titles"],
    }),
    getFocusHistory: builder.query({
      query: ({ days = 30, limit = 50 } = {}) => ({
        url: "/api/daily-logs/my-focus-sessions",
        method: "POST",
        data: { days, limit },
      }),
      providesTags: ["FocusHistory"],
    }),
  }),
});

export const {
  useGetMyTitlesQuery,
  useGetMyKazaQuery,
  useUpdateKazaSettingsMutation,
  useSetKazaDayMutation,
  useGetMyWeekMuzakeresQuery,
  useSetMuzakereDoneMutation,
  useGetAssessmentQuery,
  useSubmitAssessmentMutation,
  useAddFocusSessionMutation,
  useGetFocusHistoryQuery,
} = personalApi;
